/**
 * Notifications module
 * Toast notifications and alert system
 */

/**
 * Default notification options
 */
const defaultOptions = {
    position: 'top-right',
    duration: 5000,
    closable: true,
    progress: true,
    pauseOnHover: true
};

/**
 * Notification container
 */
let container = null;

/**
 * Initialize notifications module
 */
export function init() {
    // Module is ready to use - no special initialization needed
}

/**
 * Get or create notification container
 * @param {string} position - Position class
 * @returns {HTMLElement} Container element
 */
function getContainer(position = 'top-right') {
    if (!container) {
        container = document.createElement('div');
        container.id = 'notification-container';
        container.className = 'fixed z-50 pointer-events-none';
        
        // Set position classes
        const positions = {
            'top-right': 'top-20 right-4',
            'top-left': 'top-20 left-4',
            'bottom-right': 'bottom-4 right-4',
            'bottom-left': 'bottom-4 left-4',
            'top-center': 'top-20 left-1/2 transform -translate-x-1/2',
            'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
        };
        
        container.className += ` ${positions[position] || positions['top-right']}`;
        document.body.appendChild(container);
    }
    
    return container;
}

/**
 * Create notification element
 * @param {string} message - Notification message
 * @param {string} type - Notification type (success, error, warning, info)
 * @param {Object} options - Notification options
 * @returns {HTMLElement} Notification element
 */
function createNotification(message, type = 'info', options = {}) {
    const notification = document.createElement('div');
    notification.className = 'pointer-events-auto mb-4 transform transition-all duration-300 ease-in-out';
    
    // Type-specific classes
    const typeClasses = {
        success: 'bg-green-50 text-green-800 border-green-200',
        error: 'bg-red-50 text-red-800 border-red-200',
        warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
        info: 'bg-blue-50 text-blue-800 border-blue-200'
    };
    
    // Icons for each type
    const icons = {
        success: `<svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>`,
        error: `<svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>`,
        warning: `<svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>`,
        info: `<svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
        </svg>`
    };
    
    notification.innerHTML = `
        <div class="max-w-sm w-full bg-white shadow-lg rounded-lg border ${typeClasses[type] || typeClasses.info}">
            <div class="p-4">
                <div class="flex items-start">
                    <div class="shrink-0">
                        ${icons[type] || icons.info}
                    </div>
                    <div class="ml-3 w-0 flex-1">
                        <p class="text-sm font-medium">${message}</p>
                    </div>
                    ${options.closable ? `
                        <div class="ml-4 shrink-0 flex">
                            <button class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                <span class="sr-only">Close</span>
                                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    ` : ''}
                </div>
            </div>
            ${options.progress ? `
                <div class="h-1 bg-gray-200 rounded-b-lg overflow-hidden">
                    <div class="h-full bg-current transition-all duration-linear" style="transition-duration: ${options.duration}ms; width: 100%;"></div>
                </div>
            ` : ''}
        </div>
    `;
    
    // Add close functionality
    if (options.closable) {
        const closeBtn = notification.querySelector('button');
        closeBtn.addEventListener('click', () => removeNotification(notification));
    }
    
    return notification;
}

/**
 * Show notification
 * @param {HTMLElement} notification - Notification element
 * @param {Object} options - Notification options
 */
function showNotification(notification, options) {
    const container = getContainer(options.position);
    
    // Add to container
    container.appendChild(notification);
    
    // Trigger animation
    requestAnimationFrame(() => {
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100%)';
        
        requestAnimationFrame(() => {
            notification.style.opacity = '1';
            notification.style.transform = 'translateX(0)';
        });
    });
    
    // Auto remove after duration
    if (options.duration > 0) {
        let timeoutId = setTimeout(() => removeNotification(notification), options.duration);
        
        // Pause on hover
        if (options.pauseOnHover) {
            notification.addEventListener('mouseenter', () => {
                clearTimeout(timeoutId);
                const progressBar = notification.querySelector('.bg-current');
                if (progressBar) {
                    progressBar.style.animationPlayState = 'paused';
                }
            });
            
            notification.addEventListener('mouseleave', () => {
                timeoutId = setTimeout(() => removeNotification(notification), options.duration);
                const progressBar = notification.querySelector('.bg-current');
                if (progressBar) {
                    progressBar.style.animationPlayState = 'running';
                }
            });
        }
    }
}

/**
 * Remove notification
 * @param {HTMLElement} notification - Notification element
 */
function removeNotification(notification) {
    notification.style.opacity = '0';
    notification.style.transform = 'translateX(100%)';
    
    setTimeout(() => {
        notification.remove();
        
        // Remove container if empty
        if (container && container.children.length === 0) {
            container.remove();
            container = null;
        }
    }, 300);
}

/**
 * Show success notification
 * @param {string} message - Notification message
 * @param {Object} options - Notification options
 */
export function success(message, options = {}) {
    const opts = { ...defaultOptions, ...options };
    const notification = createNotification(message, 'success', opts);
    showNotification(notification, opts);
}

/**
 * Show error notification
 * @param {string} message - Notification message
 * @param {Object} options - Notification options
 */
export function error(message, options = {}) {
    const opts = { ...defaultOptions, ...options };
    const notification = createNotification(message, 'error', opts);
    showNotification(notification, opts);
}

/**
 * Show warning notification
 * @param {string} message - Notification message
 * @param {Object} options - Notification options
 */
export function warning(message, options = {}) {
    const opts = { ...defaultOptions, ...options };
    const notification = createNotification(message, 'warning', opts);
    showNotification(notification, opts);
}

/**
 * Show info notification
 * @param {string} message - Notification message
 * @param {Object} options - Notification options
 */
export function info(message, options = {}) {
    const opts = { ...defaultOptions, ...options };
    const notification = createNotification(message, 'info', opts);
    showNotification(notification, opts);
}

/**
 * Show custom notification
 * @param {string} message - Notification message
 * @param {string} type - Notification type
 * @param {Object} options - Notification options
 */
export function notify(message, type = 'info', options = {}) {
    const opts = { ...defaultOptions, ...options };
    const notification = createNotification(message, type, opts);
    showNotification(notification, opts);
}

/**
 * Clear all notifications
 */
export function clearAll() {
    if (container) {
        const notifications = container.querySelectorAll('div > div');
        notifications.forEach(notification => removeNotification(notification.parentElement));
    }
}

/**
 * Show confirmation dialog
 * @param {string} message - Confirmation message
 * @param {Object} options - Dialog options
 * @returns {Promise<boolean>} User's choice
 */
export function confirm(message, options = {}) {
    return new Promise((resolve) => {
        const {
            title = 'Confirm',
            confirmText = 'Confirm',
            cancelText = 'Cancel',
            type = 'warning'
        } = options;
        
        // Create modal backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'fixed inset-0 bg-gray-500 bg-gray-500/75 transition-opacity z-50 flex items-center justify-center p-4';
        
        // Create modal
        const modal = document.createElement('div');
        modal.className = 'bg-white rounded-lg shadow-xl transform transition-all max-w-md w-full';
        
        modal.innerHTML = `
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="shrink-0">
                        ${type === 'warning' ? `
                            <svg class="h-6 w-6 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        ` : `
                            <svg class="h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        `}
                    </div>
                    <h3 class="ml-3 text-lg font-medium text-gray-900">${title}</h3>
                </div>
                <p class="text-sm text-gray-500">${message}</p>
            </div>
            <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse space-x-2 space-x-reverse">
                <button type="button" class="btn-primary confirm-btn">${confirmText}</button>
                <button type="button" class="btn-secondary cancel-btn">${cancelText}</button>
            </div>
        `;
        
        // Add event listeners
        const confirmBtn = modal.querySelector('.confirm-btn');
        const cancelBtn = modal.querySelector('.cancel-btn');
        
        confirmBtn.addEventListener('click', () => {
            backdrop.remove();
            resolve(true);
        });
        
        cancelBtn.addEventListener('click', () => {
            backdrop.remove();
            resolve(false);
        });
        
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) {
                backdrop.remove();
                resolve(false);
            }
        });
        
        // Add to DOM
        backdrop.appendChild(modal);
        document.body.appendChild(backdrop);
        
        // Focus confirm button
        confirmBtn.focus();
    });
}